import React, { useState, useEffect } from 'react';
import { Usuario, Empresa, Grupo } from '../../types/entities';
import { usuarioService, empresaService, grupoService } from '../../services/entitiesServices';
import { useNotifier } from '../ui/Notifier';

interface UsuarioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  usuario?: Usuario | null;
}

export const UsuarioModal: React.FC<UsuarioModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  usuario
}) => {
  const [loading, setLoading] = useState(false);
  const notifier = useNotifier();
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    ehGrupo: false,
    empresaId: '',
    grupoId: ''
  });

  // Resetar formulário quando o modal abrir/fechar ou usuário mudar
  useEffect(() => {
    if (isOpen) {
      console.log('🔧 Modal de usuário aberto, carregando dados...');
      console.log('👤 Usuário para edição:', usuario);
      
      if (usuario) {
        setFormData({
          nome: usuario.nome,
          email: usuario.email,
          senha: '',
          ehGrupo: usuario.ehGrupo,
          empresaId: usuario.empresaId || '',
          grupoId: usuario.grupoId || ''
        });
      } else {
        setFormData({
          nome: '',
          email: '',
          senha: '',
          ehGrupo: false,
          empresaId: '',
          grupoId: ''
        });
      }
      
      console.log('📞 Iniciando carregamento de empresas e grupos...');
      loadEmpresas();
      loadGrupos();
    }
  }, [isOpen, usuario]);

  const loadEmpresas = async () => {
    setLoadingEmpresas(true);
    try {
      console.log('🔄 Carregando empresas para seleção...');
      const response = await empresaService.list();
      console.log('📡 Resposta da API de empresas:', response);
      console.log('📡 Tipo da resposta:', typeof response, Array.isArray(response));
      
      // Tratamento mais robusto da resposta da API
      let empresasData = [];
      
      if (Array.isArray(response)) {
        // Se a resposta é um array direto
        empresasData = response;
        console.log('📋 Empresas (array direto):', empresasData);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Se a resposta tem formato { data: [...] }
        empresasData = response.data;
        console.log('📋 Empresas (response.data):', empresasData);
      } else if (response && (response as any).empresas && Array.isArray((response as any).empresas)) {
        // Se a resposta tem formato { empresas: [...] }
        empresasData = (response as any).empresas;
        console.log('📋 Empresas (response.empresas):', empresasData);
      } else {
        console.log('📋 Formato de resposta não reconhecido, tentando Object.values...');
        const possibleArrays = Object.values(response || {}).filter(Array.isArray);
        if (possibleArrays.length > 0) {
          empresasData = possibleArrays[0];
          console.log('📋 Empresas (Object.values):', empresasData);
        }
      }
      
      console.log('📋 Empresas finais:', empresasData, 'Quantidade:', empresasData.length);
      setEmpresas(empresasData);
    } catch (err) {
      console.error('❌ Erro ao carregar empresas:', err);
      setEmpresas([]);
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const loadGrupos = async () => {
    setLoadingGrupos(true);
    try {
      console.log('🔄 Carregando grupos para seleção...');
      const response = await grupoService.list();
      console.log('📡 Resposta da API de grupos:', response);
      console.log('📡 Tipo da resposta grupos:', typeof response, Array.isArray(response));
      
      // Tratamento mais robusto da resposta da API
      let gruposData = [];
      
      if (Array.isArray(response)) {
        // Se a resposta é um array direto
        gruposData = response;
        console.log('📋 Grupos (array direto):', gruposData);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Se a resposta tem formato { data: [...] }
        gruposData = response.data;
        console.log('📋 Grupos (response.data):', gruposData);
      } else if (response && (response as any).grupos && Array.isArray((response as any).grupos)) {
        // Se a resposta tem formato { grupos: [...] }
        gruposData = (response as any).grupos;
        console.log('📋 Grupos (response.grupos):', gruposData);
      } else {
        console.log('📋 Formato de resposta grupos não reconhecido, tentando Object.values...');
        const possibleArrays = Object.values(response || {}).filter(Array.isArray);
        if (possibleArrays.length > 0) {
          gruposData = possibleArrays[0];
          console.log('📋 Grupos (Object.values):', gruposData);
        }
      }
      
      console.log('📋 Grupos finais:', gruposData, 'Quantidade:', gruposData.length);
      setGrupos(gruposData);
    } catch (err) {
      console.error('❌ Erro ao carregar grupos:', err);
      setGrupos([]);
    } finally {
      setLoadingGrupos(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.email) {
      notifier.show({ message: 'Nome e email são obrigatórios', severity: 'warning' });
      return;
    }

    if (!usuario && !formData.senha) {
      notifier.show({ message: 'Senha é obrigatória para novos usuários', severity: 'warning' });
      return;
    }

    setLoading(true);
    
    try {
      if (usuario) {
        // Atualizar usuário existente
        const updateData = {
          nome: formData.nome,
          email: formData.email,
          ehGrupo: formData.ehGrupo,
          empresaId: formData.empresaId || undefined,
          grupoId: formData.grupoId || undefined
        };
        await usuarioService.update(usuario.id, updateData);
      } else {
        // Criar novo usuário
        const createData = {
          nome: formData.nome,
          email: formData.email,
          senha: formData.senha,
          ehGrupo: formData.ehGrupo,
          empresaId: formData.empresaId || undefined,
          grupoId: formData.grupoId || undefined
        };
        await usuarioService.create(createData);
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      notifier.show({ message: 'Erro ao salvar usuário', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">
            {usuario ? 'Editar Usuário' : 'Novo Usuário'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome *
              </label>
              <input
                type="text"
                required
                value={formData.nome}
                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Nome completo do usuário"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-mail *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="usuario@exemplo.com"
              />
            </div>

            {!usuario && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senha *
                </label>
                <input
                  type="password"
                  required={!usuario}
                  value={formData.senha}
                  onChange={(e) => setFormData(prev => ({ ...prev, senha: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Senha do usuário"
                  minLength={6}
                />
              </div>
            )}

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.ehGrupo}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    ehGrupo: e.target.checked,
                    // Limpar empresaId quando for usuário de grupo
                    empresaId: e.target.checked ? '' : prev.empresaId
                  }))}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Usuário de Grupo
                </span>
              </label>
              {formData.ehGrupo && (
                <p className="text-xs text-purple-600 mt-1">
                  Usuários de grupo não podem ser vinculados diretamente a empresas
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Empresa {process.env.NODE_ENV === 'development' && `(${empresas.length} encontradas)`}
              </label>
              <select
                value={formData.empresaId}
                onChange={(e) => setFormData(prev => ({ ...prev, empresaId: e.target.value }))}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  formData.ehGrupo ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                disabled={loadingEmpresas || formData.ehGrupo}
              >
                <option value="">
                  {loadingEmpresas 
                    ? 'Carregando empresas...' 
                    : formData.ehGrupo 
                      ? 'Desabilitado para usuários de grupo'
                      : 'Selecione uma empresa (opcional)'
                  }
                </option>
                {!loadingEmpresas && !formData.ehGrupo && empresas.map((empresa) => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.nome}
                  </option>
                ))}
              </select>
              {loadingEmpresas && (
                <div className="flex items-center mt-1 text-sm text-gray-500">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Carregando empresas disponíveis...
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grupo {process.env.NODE_ENV === 'development' && `(${grupos.length} encontrados)`}
              </label>
              <select
                value={formData.grupoId}
                onChange={(e) => setFormData(prev => ({ ...prev, grupoId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={loadingGrupos}
              >
                <option value="">
                  {loadingGrupos ? 'Carregando grupos...' : 'Selecione um grupo (opcional)'}
                </option>
                {!loadingGrupos && grupos.map((grupo) => (
                  <option key={grupo.id} value={grupo.id}>
                    {grupo.nome}
                  </option>
                ))}
              </select>
              {loadingGrupos && (
                <div className="flex items-center mt-1 text-sm text-gray-500">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Carregando grupos disponíveis...
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-6">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-2 px-4 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : (usuario ? 'Atualizar' : 'Criar Usuário')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};